import axios, { AxiosRequestConfig, Method } from "axios";

const API_BASE_URL: string =
  process.env.REACT_APP_API_BASE_URL || "https://backend.risewayindia.com/api/admin";
const BACKEND_ORIGIN: string = API_BASE_URL.replace(/\/api\/admin\/?$/, "");

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatCurrency = (value: number | string | null | undefined): string =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export const getImageUrl = (image: string | null | undefined): string => {
  if (typeof image !== "string" || !image) return "";

  if (/^(https?:|data:|blob:)/i.test(image)) return image;

  return `${BACKEND_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
};

export const getProfileImage = (profileData: Record<string, any> | null | undefined): string => {
  return getImageUrl(profileData?.profile);
};

const normalizeUploadUrls = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(normalizeUploadUrls);
  }

  if (!value || typeof value !== "object") return value;

  Object.entries(value).forEach(([key, fieldValue]) => {
    if (key === "image" || key === "profile") {
      value[key] = getImageUrl(fieldValue as string);
      return;
    }

    if (key === "images" && Array.isArray(fieldValue)) {
      value[key] = fieldValue.map(getImageUrl);
      return;
    }

    value[key] = normalizeUploadUrls(fieldValue);
  });

  return value;
};

type LoaderListener = (pendingCount: number) => void;

let pendingRequests = 0;
const loaderListeners = new Set<LoaderListener>();

const setPendingRequests = (count: number): void => {
  pendingRequests = Math.max(0, count);
  loaderListeners.forEach((listener) => listener(pendingRequests));
};

export const subscribeApiLoader = (listener: LoaderListener): (() => void) => {
  loaderListeners.add(listener);
  listener(pendingRequests);
  return () => {
    loaderListeners.delete(listener);
  };
};

declare module "axios" {
  interface AxiosRequestConfig {
    skipLoader?: boolean;
  }
}

const finishTrackedRequest = (config?: AxiosRequestConfig): void => {
  if (config && !config.skipLoader) {
    setPendingRequests(pendingRequests - 1);
  }
};

apiClient.interceptors.request.use(
  (config) => {
    if (!config.skipLoader) {
      setPendingRequests(pendingRequests + 1);
    }
    const token = localStorage.getItem("adminToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const isAuthSessionError = (status: number | undefined, message: string = ""): boolean => {
  const normalized = String(message).toLowerCase();

  if (status === 401) return true;

  return (
    normalized.includes("json web token is expired") ||
    normalized.includes("json web token is invalid") ||
    normalized.includes("session expired") ||
    normalized.includes("please login again") ||
    normalized.includes("please login as admin")
  );
};

const clearAdminSession = (): void => {
  const hadToken = Boolean(localStorage.getItem("adminToken"));
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminProfile");

  if (hadToken && typeof window !== "undefined") {
    // Let AuthContext react immediately so guarded routes show Sign In.
    window.dispatchEvent(new Event("auth:unauthorized"));
  }
};

apiClient.interceptors.response.use(
  (response) => {
    finishTrackedRequest(response.config);
    response.data = normalizeUploadUrls(response.data);
    return response;
  },
  (error) => {
    finishTrackedRequest(error.config);
    const payload = error.response?.data;
    const status: number | undefined = error.response?.status;
    const message: string = payload?.message || error.message || "";

    // Skip auto-logout on login/auth endpoints so wrong password
    // does not clear an existing session mid-flow.
    const requestUrl = String(error.config?.url || "");
    const isAuthEndpoint =
      requestUrl.includes("/login") || requestUrl.endsWith("/logout");

    if (!isAuthEndpoint && isAuthSessionError(status, message)) {
      clearAdminSession();
    }

    return Promise.reject(payload || { message: message || "Request failed" });
  },
);

export const apiRequest = async (
  endpoint: string,
  method?: Method | string,
  data?: any,
  headers?: Record<string, string>,
  options?: { skipLoader?: boolean },
): Promise<any> => {
  const requestHeaders: Record<string, string> = { ...(headers || {}) };
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    requestHeaders["Content-Type"] = "multipart/form-data";
  }

  const config: AxiosRequestConfig = {
    url: endpoint,
    method: (method || "GET") as Method,
    data: data ?? undefined,
    headers: requestHeaders,
    skipLoader: options?.skipLoader,
  };
  const response = await apiClient(config);
  return response.data;
};
